import { CloseOutlined, LeftOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Dropdown, Popconfirm, Progress, Tooltip } from "antd"
import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { isEmpty, trim } from "lodash"
import * as com from "../Common"
import { getPropertyAddress } from "./propertyAddress"
import { showPrequalLetterMenuV2 } from "./showPrequalLetterMenuV2"
import { effectiveCompletedStepsForDisplay } from "./completedStepsProgress"
import { CheckSignInMethod } from "../newContent/api/api"
import { track } from "../statsig"
import { STATSIG, loanTabValue } from "../statsig/events"
import "../newContent/page/styles/newSideBarApplication.css"
import "../newContent/page/styles/loans.css"
import "./styles.css"

/** Normalize LOS / split-table stage to CurrentStage keys (Application|Processing|Closing|Disposed). */
const normalizeLoanStage = (stage) => {
    const raw = `${stage || ""}`.trim()
    if (!raw) return "Application"
    if (com.CurrentStage[raw] != null) return raw
    const pascal = raw
        .split(/[-_\s]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join("")
    if (com.CurrentStage[pascal] != null) return pascal
    return "Application"
}

const fullName = (participant) => {
    const name = [participant.firstName, participant.middleName, participant.lastName]
        .filter((item) => !isEmpty(trim(item || "")))
        .join(" ")
    return name || (participant.isPrimary ? "Borrower" : "Co-borrower")
}

const borrowerRoleLabel = (participant) => (
    participant.isPrimary ? "Primary borrower" : "Co-borrower"
)

const participantSummary = (participant) => (
    <Tooltip placement="right" title={fullName(participant)}>
        <span className="application-v2-party-copy">
            <span className="application-v2-party-name">{fullName(participant)}</span>
            <span className="application-v2-party-role">{borrowerRoleLabel(participant)}</span>
        </span>
    </Tooltip>
)

const progressIndicator = (participant, application) => {
    const completedSteps = effectiveCompletedStepsForDisplay(application, participant)
    const values = Object.values(completedSteps)
    const completedCount = values.filter(Boolean).length
    const storedHasProgress = Object.values(participant?.completedSteps || {}).some(Boolean)

    // Truly new applicant with a name but no stored steps and no inferred field data.
    if (
        completedCount === 0
        && !storedHasProgress
        && fullName(participant) !== "Borrower"
        && fullName(participant) !== "Co-borrower"
    ) {
        return (
            <div className="progress-wrapper">
                <Progress
                    type="circle"
                    percent={0}
                    size={28}
                    strokeWidth={10}
                    format={() => null}
                    strokeColor="#325CEB"
                    trailColor="#BDCBFB"
                />
                <div className="progress-text">NEW</div>
            </div>
        )
    }

    const percent = values.length ? Math.round((completedCount / values.length) * 100) : 0
    if (percent === 100) {
        return (
            <img
                src="/images/sidebaricons/blue-check-mark.svg"
                alt="completed"
                className="sub-step-icon"
            />
        )
    }

    return (
        <div className="progress-wrapper">
            <Progress
                type="circle"
                percent={percent}
                size={28}
                strokeWidth={10}
                format={() => null}
                strokeColor="#325CEB"
                trailColor="#BDCBFB"
            />
            <div className="progress-text">{percent}%</div>
        </div>
    )
}

const SidebarV2 = ({
    application,
    activePartyId,
    activeSection,
    onAddCoborrower,
    onDeleteCoborrower,
    addingCoborrower,
    deletingCoborrowerPartyId,
    variant = "inline",
    onNavigate,
}) => {
    const [method, setMethod] = useState("")
    const participants = useMemo(() => application?.participantOrder || [], [application])
    const participantsById = useMemo(() => application?.participantsById || {}, [application])
    const primary = useMemo(
        () => participants.map((id) => participantsById[id]).find((item) => item?.isPrimary),
        [participants, participantsById]
    )
    const propertyAddress = useMemo(
        () => getPropertyAddress(application?.property),
        [application?.property]
    )

    const loanCurrentStage = useMemo(
        () => normalizeLoanStage(application?.currentStage),
        [application?.currentStage]
    )
    const isProcessingActive = com.CurrentStage[loanCurrentStage] >= com.CurrentStage.Processing
    const isClosingActive = com.CurrentStage[loanCurrentStage] === com.CurrentStage.Closing

    const dropdownMenuItems = useMemo(
        () => [
            {
                label: (
                    <Link
                        to={method === "email" ? "/application/password" : "/application/changepassword"}
                        className="DropdownP"
                    >
                        {method === "email" ? "Set up password" : "Change password"}
                    </Link>
                ),
                key: "1",
            },
            { type: "divider" },
            {
                label: (
                    <Link to="/application/logout" className="DropdownP">
                        Sign out
                    </Link>
                ),
                key: "3",
            },
        ],
        [method]
    )

    useEffect(() => {
        let isMounted = true

        const email = primary?.email
        const fetchSignInMethod = async () => {
            if (!email) return
            try {
                const { Method } = await CheckSignInMethod({
                    CustomerID: sessionStorage.getItem("cid"),
                    Email: email,
                })
                if (isMounted) setMethod(Method)
            } catch (error) {
                console.error("Error fetching sign-in method:", error)
            }
        }

        fetchSignInMethod()
        return () => {
            isMounted = false
        }
    }, [primary?.email])

    const sidebarClassName = variant === "drawer"
        ? "side-bar-container application-v2-sidebar application-v2-sidebar-drawer-panel"
        : "side-bar-container application-v2-sidebar application-v2-sidebar-inline"

    const handleNavigate = () => {
        onNavigate?.()
    }

    /** Align with LOS LOAN_TAB：切左侧菜单才打，同 tab 重复点不打。 */
    const trackSidebarTab = (nextSection) => {
        const nextTab = loanTabValue(nextSection)
        if (loanTabValue(activeSection) === nextTab) {
            return
        }
        track(STATSIG.LOAN.TAB, nextTab, { tab: nextTab })
    }

    const trackPartyNavigate = (partyId, role) => {
        trackSidebarTab("yourinfo")
        if (partyId && partyId !== activePartyId) {
            track(STATSIG.LOAN.BORROWER_SWITCH, partyId, {
                party_id: partyId,
                role,
            })
        }
    }

    return (
        <div className={sidebarClassName}>
            <div className="application-v2-sidebar-main">
                <div className="w-100 d-flex align-items-center position-relative company-name-container">
                    <div className="company-name">{application?.companyName || ""}</div>
                    <div className="company-name-underline" />
                </div>

                <div
                    className="text-left back-to-loans"
                    onClick={() => {
                        handleNavigate()
                        window.open("/application/myloans", "_self")
                    }}
                >
                    <LeftOutlined className="back-to-loans-icon" />
                    <span className="back-to-loans-text">Back to Loans</span>
                </div>

                <Link
                    to="/application/v2/interview/overview"
                    className="d-flex align-items-start application-overview"
                    style={{ backgroundColor: activeSection === "overview" ? "#E0E7FF" : "#F6F6F6" }}
                    onClick={() => {
                        trackSidebarTab("overview")
                        handleNavigate()
                    }}
                >
                    <img src="/house.svg" alt="house" />
                    <div className="text-left" style={{ marginLeft: "10px" }}>
                        <div className="overview-title" style={{ color: "#222222" }}>
                            OVERVIEW
                        </div>
                        <div className="address-text">{propertyAddress}</div>
                    </div>
                </Link>

                <div className="sidebar-steps application-v2-sidebar-steps">
                    <div className="application-v2-sidebar-elastic">
                    <div className="custom-step application-v2-application-step">
                        <div className="step-header">
                            <div className="step-number active">1</div>
                            <div className="step-title active">APPLICATION</div>
                        </div>
                        <div className="step-content active application-v2-application-step-content">
                            {primary && (
                                <Link
                                    to={`/application/v2/interview/yourinfo/${primary.partyId}`}
                                    className={`sub-step application-v2-party-step ${activePartyId === primary.partyId ? "active" : ""}`}
                                    onClick={() => {
                                        trackPartyNavigate(primary.partyId, "borrower")
                                        handleNavigate()
                                    }}
                                >
                                    {participantSummary(primary)}
                                    {progressIndicator(primary, application)}
                                </Link>
                            )}

                            {participants
                                .map((id) => participantsById[id])
                                .filter((item) => item && !item.isPrimary)
                                .map((participant) => {
                                    return (
                                        <div
                                            key={participant.partyId}
                                            className={`sub-step application-v2-party-step ${activePartyId === participant.partyId ? "active" : ""}`}
                                        >
                                            <Link
                                                to={`/application/v2/interview/yourinfo/${participant.partyId}`}
                                                className="application-v2-party-link"
                                                onClick={() => {
                                                    trackPartyNavigate(participant.partyId, "coborrower")
                                                    handleNavigate()
                                                }}
                                            >
                                                {participantSummary(participant)}
                                            </Link>
                                            <Popconfirm
                                                title="Delete this co-borrower?"
                                                okText="Delete"
                                                cancelText="Cancel"
                                                placement="right"
                                                onConfirm={() => onDeleteCoborrower(participant.partyId)}
                                            >
                                                <Button
                                                    type="text"
                                                    aria-label={`Delete ${fullName(participant)}`}
                                                    icon={<CloseOutlined />}
                                                    loading={deletingCoborrowerPartyId === participant.partyId}
                                                    className="application-v2-delete-coborrower"
                                                />
                                            </Popconfirm>
                                            {progressIndicator(participant, application)}
                                        </div>
                                    )
                                })}

                            <Button
                                type="text"
                                icon={<PlusOutlined />}
                                loading={addingCoborrower}
                                onClick={onAddCoborrower}
                                className="application-v2-add-coborrower"
                            >
                                Add co-borrower
                            </Button>

                            <Link
                                to="/application/v2/interview/rates"
                                className={`sub-step ${activeSection === "rates" ? "active" : ""}`}
                                onClick={() => {
                                    trackSidebarTab("rates")
                                    handleNavigate()
                                }}
                            >
                                <span>Rates</span>
                            </Link>
                            {showPrequalLetterMenuV2(application) && (
                                <Link
                                    to="/application/v2/interview/prequalletter"
                                    className={`sub-step ${activeSection === "prequalletter" ? "active" : ""}`}
                                    onClick={() => {
                                        trackSidebarTab("prequalletter")
                                        handleNavigate()
                                    }}
                                >
                                    <span>Pre-qual letter</span>
                                </Link>
                            )}
                        </div>
                    </div>

                    <div className="custom-step processing-step application-v2-processing-step">
                        <div className="step-header">
                            <div className="step-number active">2</div>
                            <div className={`step-title ${isProcessingActive ? "active" : ""}`}>PROCESSING</div>
                        </div>
                        <div className={`step-content ${isProcessingActive ? "active" : ""}`}>
                            <Link
                                to="/application/v2/interview/documents"
                                className={`sub-step ${activeSection === "documents" ? "active" : ""}`}
                                onClick={() => {
                                    trackSidebarTab("documents")
                                    handleNavigate()
                                }}
                            >
                                <span>Documents</span>
                            </Link>
                        </div>
                    </div>

                    <div className="custom-step closing-step application-v2-closing-step">
                        <div
                            className="step-header"
                            style={{ cursor: isClosingActive ? "default" : "not-allowed" }}
                        >
                            <div
                                className="step-number active"
                                style={{ backgroundColor: isClosingActive ? "#081265" : "#D9D9D9" }}
                            >
                                3
                            </div>
                            <div
                                className={`step-title ${isClosingActive ? "active" : ""}`}
                                style={{ color: isClosingActive ? "#081265" : "#6E6E70" }}
                            >
                                CLOSING
                            </div>
                        </div>
                    </div>
                    </div>
                </div>
            </div>

            <div className="sidebar-footer application-v2-sidebar-footer">
                <div className="sidebar-footer-divider" />
                <div className="sidebar-footer-content">
                    <Dropdown menu={{ items: dropdownMenuItems }}>
                        <div className="d-flex align-items-center pt-2 pb-2">
                            <div className="NameBar">{(primary?.firstName || "B").charAt(0)}</div>
                            <div style={{ flex: 1, minWidth: 0, overflow: "hidden", marginLeft: "10px" }}>
                                <Tooltip placement="right" title={primary ? fullName(primary) : ""}>
                                    <div className="text-left overflow-h user-full-name">
                                        {primary ? fullName(primary) : ""}
                                    </div>
                                </Tooltip>
                                <Tooltip placement="right" title={primary?.email || ""}>
                                    <div className="text-left overflow-h user-email">
                                        {primary?.email || ""}
                                    </div>
                                </Tooltip>
                            </div>
                        </div>
                    </Dropdown>
                </div>
            </div>
        </div>
    )
}

export default SidebarV2
